/**
 * Minimal anonymous event tracking for the DTP storefront. Fires to
 * POST /api/dtp/analytics. Errors are swallowed — we never want telemetry
 * to break the user-facing flow.
 */
import { dtpApi } from './dtpApi';
import { track } from './analytics';
import type { AnalyticsEventName } from '@bloom/shared';

export type DtpEventType =
  | 'landing_view'
  | 'category_view'
  | 'family_view'
  | 'dose_finder_complete'
  | 'add_to_cart'
  | 'session_start'
  | 'checkout_step'
  | 'order_created';

type StoreVariant = 'lightweight' | 'educational' | 'custom';

interface DtpEventPayload {
  physicianId: string;
  variant: StoreVariant;
  eventType: DtpEventType;
  categorySlug?: string;
  familySlug?: string;
  productId?: string;
  source?: string;
  patientId?: string;
  referrer?: string;
  step?: string;
  orderId?: string;
  entryPath?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/** The same events in the patient analytics pipeline (lib/analytics.ts), named object_action. */
const BRIDGED_NAMES: Record<DtpEventType, AnalyticsEventName> = {
  landing_view: 'storefront_landing_viewed',
  category_view: 'category_viewed',
  family_view: 'product_family_viewed',
  dose_finder_complete: 'dose_finder_completed',
  add_to_cart: 'cart_item_added',
  session_start: 'storefront_session_started',
  checkout_step: 'checkout_step_viewed',
  order_created: 'checkout_order_placed',
};

const SESSION_KEY = 'dtp-session-id';

function getSessionId(): string {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`).slice(0, 64);
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
}

export function trackDtpEvent(payload: DtpEventPayload): void {
  if (!payload.physicianId || !payload.eventType) return;
  const body = {
    ...payload,
    sessionId: getSessionId(),
    referrer: payload.referrer ?? (typeof document !== 'undefined' ? document.referrer || undefined : undefined),
  };
  // Fire and forget; ignore failures
  dtpApi.postAnalytics(body).catch(() => {});
  // Bridged into the patient analytics store, which adds device, attribution
  // and the signed-in patient. Ids/slugs only: the registry drops the rest.
  const { physicianId: _clinic, eventType, patientId: _patient, referrer: _referrer, ...properties } = payload;
  void _clinic; void _patient; void _referrer;
  track(BRIDGED_NAMES[eventType], properties);
}

// ---------------------------------------------------------------------------
// Store sessions. Every storefront entry point records the clinic it loaded
// (via the cart store's applyClinicFromCatalog), which starts the session once
// per browser-tab session and clinic. Events from pages that don't know the
// store variant go through trackStoreEvent, which waits for that context.

const CONTEXT_KEY = 'dtp-store-context';
const STARTED_KEY = 'dtp-session-started';
type StoreContext = { physicianId: string; variant: StoreVariant };
type StoreEvent = Omit<DtpEventPayload, 'physicianId' | 'variant'>;
let pending: StoreEvent[] = [];

function readContext(): StoreContext | null {
  try {
    const raw = window.sessionStorage.getItem(CONTEXT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoreContext(physicianId: string | undefined, variant: StoreVariant | undefined): void {
  if (!physicianId || !variant) return;
  const context = { physicianId, variant };
  try {
    window.sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
    if (window.sessionStorage.getItem(STARTED_KEY) !== physicianId) {
      window.sessionStorage.setItem(STARTED_KEY, physicianId);
      const params = new URLSearchParams(window.location.search);
      trackDtpEvent({
        ...context,
        eventType: 'session_start',
        entryPath: window.location.pathname,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined,
      });
    }
  } catch {
    // Storage blocked: still send what we have, without the once-per-session guard.
  }
  const queued = pending;
  pending = [];
  for (const event of queued) trackDtpEvent({ ...context, ...event });
}

/** Track an event against the current store; held until the store context is known. */
export function trackStoreEvent(eventType: DtpEventType, extra: Omit<StoreEvent, 'eventType'> = {}): void {
  const context = readContext();
  if (context) trackDtpEvent({ ...context, eventType, ...extra });
  else if (pending.length < 20) pending.push({ eventType, ...extra });
}
